import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Menu, X, Globe, User, LogOut, LayoutDashboard, Plus, ChevronDown } from 'lucide-react';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { ContactUsButton } from '@/components/support/ContactUsButton';
import { useCurrency } from '@/contexts/CurrencyContext';
import logo from '@/assets/logo.png';

const languages = [
  { code: 'en', label: 'English' },
  { code: 'am', label: 'አማርኛ' },
  { code: 'om', label: 'Afaan Oromoo' },
];

const topBarLinks = [
  { to: '/plots', label: 'Plot Finder' },
  { to: '/area-guides', label: 'Area Guide' },
  { to: '#', label: 'Blogs' },
  { to: '#', label: 'Maps' },
];

const categoryNavLinks = [
  { to: '/buy', label: 'HOMES' },
  { to: '/plots', label: 'PLOTS' },
  { to: '/commercial', label: 'COMMERCIAL' },
  { to: '/rent', label: 'RENT' },
  { to: '/agencies', label: 'AGENCIES' },
  { to: '/projects', label: 'NEW PROJECTS' },
];

export function Header() {
  const { t, i18n } = useTranslation();
  const { user, signOut, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { displayCurrency, setDisplayCurrency, usdToEtb } = useCurrency();

  const isActive = (path: string) => location.pathname === path.split('?')[0];

  const fontStyle = { fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" };

  return (
    <header className="sticky top-0 z-50">
      {/* ── TOP BAR (desktop only - Zameen style) ── */}
      <div className="hidden lg:block bg-primary">
        <div className="container flex h-10 items-center justify-between">
          {/* Left: Utility Links */}
          <nav className="hidden md:flex items-center gap-1">
            {topBarLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="px-2.5 py-1 text-[13px] font-medium text-primary-foreground/80 hover:text-primary-foreground transition-colors uppercase tracking-wide"
                style={fontStyle}
              >
                {link.label}
              </Link>
            ))}
            {/* Tools Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-0.5 px-2.5 py-1 text-[13px] font-medium text-primary-foreground/80 hover:text-primary-foreground transition-colors uppercase tracking-wide"
                  style={fontStyle}
                >
                  Tools
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[160px]">
                <DropdownMenuItem onClick={() => navigate('/cost-calculator')}>
                  Cost Calculator
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/mortgage-calculator')}>
                  Mortgage Calculator
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Right: Lang + Auth */}
          <div className="flex items-center gap-1 ml-auto">
            <ContactUsButton variant="header" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-[12px] font-semibold text-primary-foreground/90 hover:text-primary-foreground hover:bg-primary-foreground/10 rounded transition-colors uppercase tracking-wide"
                  style={fontStyle}
                  aria-label="Change currency"
                >
                  {displayCurrency === 'USD' ? '$' : 'Br'}
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[180px]">
                <DropdownMenuItem onClick={() => setDisplayCurrency('ETB')}
                  className={displayCurrency === 'ETB' ? 'bg-primary/10 text-primary font-medium' : ''}>
                  <span className="font-bold mr-2">Br</span> Ethiopian Birr (ETB)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setDisplayCurrency('USD')}
                  className={displayCurrency === 'USD' ? 'bg-primary/10 text-primary font-medium' : ''}>
                  <span className="font-bold mr-2">$</span> US Dollar (USD)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5 text-[11px] text-muted-foreground">
                  1 USD ≈ Br {usdToEtb.toFixed(2)}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Change language" className="h-7 w-7 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10">
                  <Globe className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[140px]">
                {languages.map(lang => (
                  <DropdownMenuItem key={lang.code} onClick={() => i18n.changeLanguage(lang.code)}
                    className={i18n.language === lang.code ? 'bg-primary/10 text-primary font-medium' : ''}>
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <span className="text-[13px] text-primary-foreground/80 px-2 uppercase tracking-wide" style={fontStyle}>
                {t('nav.dashboard')}
              </span>
            ) : (
              <div className="hidden sm:flex items-center gap-0.5">
                <button
                  className="px-2.5 py-1 text-[13px] font-medium text-primary-foreground/80 hover:text-primary-foreground transition-colors uppercase tracking-wide"
                  style={fontStyle}
                  onClick={() => navigate('/login')}
                >
                  {t('nav.login')}
                </button>
                <span className="text-primary-foreground/40 text-[13px]">|</span>
                <button
                  className="px-2.5 py-1 text-[13px] font-medium text-primary-foreground/80 hover:text-primary-foreground transition-colors uppercase tracking-wide"
                  style={fontStyle}
                  onClick={() => navigate('/register')}
                >
                  {t('nav.register')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── BOTTOM BAR ── */}
      <div className="bg-white shadow-md">
        <div className="container flex h-14 md:h-16 lg:h-[77px] items-center justify-between gap-2 px-3 md:px-4 lg:px-6">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0 overflow-visible">
            <img
              src={logo}
              alt="EthioRealEstates.com"
              className="h-28 md:h-14 lg:h-52 w-auto object-contain max-w-none"
            />
          </Link>

          {/* Desktop Category Nav */}
          <nav className="hidden lg:flex items-center gap-0">
            {categoryNavLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-4 text-[13px] font-semibold tracking-wide transition-colors border-b-2 ${
                  isActive(link.to)
                    ? 'text-primary border-primary'
                    : 'text-gray-600 border-transparent hover:text-primary hover:border-primary/40'
                }`}
                style={fontStyle}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <NotificationBell />
                {(hasRole('agent') || hasRole('agency_admin') || hasRole('developer') || hasRole('superadmin')) && (
                  <Button
                    size="sm"
                    onClick={() => navigate('/dashboard/properties/new')}
                    className="hidden sm:flex gap-1.5 h-8 text-xs font-bold"
                    style={fontStyle}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {t('nav.postProperty')}
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label="User menu" className="h-9 w-9 rounded-full border border-border text-foreground hover:bg-accent">
                      <User className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[180px]">
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} className="gap-2">
                      <LayoutDashboard className="h-4 w-4" />
                      {t('nav.dashboard')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={signOut} className="gap-2 text-destructive">
                      <LogOut className="h-4 w-4" />
                      {t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button
                size="sm"
                className="hidden sm:flex h-9 text-xs font-bold"
                onClick={() => navigate('/register')}
                style={fontStyle}
              >
                {t('nav.register')}
              </Button>
            )}

            <Button
              variant="ghost"
              size="icon"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              className="lg:hidden h-9 w-9 text-foreground hover:bg-accent"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* ── MOBILE MENU ── */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-border p-3 space-y-0.5 shadow-lg max-h-[70vh] overflow-y-auto">
          {/* Utility Links */}
          <div className="flex flex-wrap gap-2 pb-3 mb-3 border-b border-border">
            {topBarLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className="text-[12px] font-medium text-muted-foreground hover:text-primary transition-colors"
                style={fontStyle}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/cost-calculator" onClick={() => setMobileOpen(false)} className="text-[12px] font-medium text-muted-foreground hover:text-primary" style={fontStyle}>Cost Calculator</Link>
            <Link to="/mortgage-calculator" onClick={() => setMobileOpen(false)} className="text-[12px] font-medium text-muted-foreground hover:text-primary" style={fontStyle}>Mortgage Calculator</Link>
          </div>

          {/* Category Nav */}
          {categoryNavLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMobileOpen(false)}
              className={`block py-2.5 px-3 rounded-lg text-sm font-semibold transition-colors ${
                isActive(link.to)
                  ? 'text-primary bg-primary/5 border-l-2 border-primary'
                  : 'text-gray-600 hover:text-primary hover:bg-primary/5'
              }`}
              style={fontStyle}
            >
              {link.label}
            </Link>
          ))}

          {!user && (
            <div className="flex gap-2 pt-3 mt-2 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => { navigate('/login'); setMobileOpen(false); }}
              >
                {t('nav.login')}
              </Button>
              <Button
                size="sm"
                className="flex-1"
                onClick={() => { navigate('/register'); setMobileOpen(false); }}
              >
                {t('nav.register')}
              </Button>
            </div>
          )}

          {/* Contact Us at the end of mobile sidebar */}
          <div className="pt-3 mt-2 border-t border-border" onClick={() => setMobileOpen(false)}>
            <ContactUsButton variant="mobile" />
          </div>
        </div>
      )}
    </header>
  );
}
